import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@b2bportal/core-types";
import { optOutOfGridV2 } from "./thunks/optOutOfGridV2";
import { useRefetchDepartureFlights } from "@/overrides/flights/components/FlightDeparturePage/useRefetchDepartureFlights";
import { GridV2RawEventName } from "@/tracking/events";
import { useEmitRbcRawEvent } from "@/tracking/hooks";

/**
 * The whole opt-out, what `GridV2OptOutModal.onOptOut` expects: record it on
 * the BFF, flip the store to V1 so every gated element disappears, fire the
 * opt-out event, re-shop so the results are priced on V1 and no V2 trip ID
 * survives. Rejects on failure so the modal shows its retryable error.
 */
export const useGridV2OptOut = () => {
  const dispatch = useDispatch<AppDispatch>();
  const emitOptedOut = useEmitRbcRawEvent(GridV2RawEventName.GridV2OptedOut);
  const refetchDepartureFlights = useRefetchDepartureFlights();

  const optOut = useCallback(async () => {
    let response;
    try {
      response = await dispatch(optOutOfGridV2()).unwrap();
    } catch (error) {
      emitOptedOut({
        properties: {
          success: false,
          failure_reason:
            typeof error === "string" ? error : String(error ?? "unknown"),
          screen: "opt_out_modal",
        },
      });
      throw error instanceof Error ? error : new Error(String(error));
    }
    // The store reads V1 here, so every event after the re-render carries
    // grid_version v1 (tracking spec: "for all later events"). This event's
    // own globals come from the pre-opt-out render unless the server's
    // properties carry them (event properties win over globals in
    // AppTrackingProvider); `success: true` is the boundary marker.
    emitOptedOut({
      properties: {
        success: true,
        screen: "opt_out_modal",
        serverProperties: response.trackingProperties?.properties,
      },
    });
    refetchDepartureFlights();
  }, [dispatch, emitOptedOut, refetchDepartureFlights]);

  return useMemo(() => ({ optOut }), [optOut]);
};
